import * as Sentry from '@sentry/react';
import { merge } from 'lodash';
import semverGte from 'semver/functions/gte';
import semverLt from 'semver/functions/lt';
import semverMajor from 'semver/functions/major';
import semverMinor from 'semver/functions/minor';
import semverSatisfies from 'semver/functions/satisfies';
import semverValid from 'semver/functions/valid';

import { latestVersionMetadata } from './__generated__latestVersionMetadata';

/**
 * ATRO-3039 (https://apollographql.atlassian.net/browse/ATRO-3039)
 * We currently don't have a mechanism to auto-update this information, so this
 * needs to be manually updated whenever server or router:
 *   * releases a new major version
 *   * releases a security patch
 *   * announces EOL for a major version
 */
const serverMetadata = {
  securityPatches: [
    { version: '2.14.2', affectedRange: '2.0.0 - 2.14.1' },
    { version: '2.25.3', affectedRange: '2.0.0 - 2.25.2' },
    { version: '2.26.2', affectedRange: '2.0.0 - 2.26.1' },
    { version: '3.6.2', affectedRange: '3.0.0 - 3.6.1' },
    { version: '3.6.6', affectedRange: '3.0.0 - 3.6.5' },
    { version: '3.7.0', affectedRange: '3.0.0 - 3.6.x' },
    { version: '3.9.0', affectedRange: '3.0.0 - 3.8.x' },
    { version: '3.10.1', affectedRange: '3.0.0 - 3.10.0' },
    { version: '3.12.1', affectedRange: '3.0.0 - 3.12.0' },
    { version: '4.7.4', affectedRange: '4.0.0 - 4.7.3' },
    { version: '4.9.3', affectedRange: '4.0.0 - 4.9.2' },
  ],
  byMajor: {
    '2': {
      eol: '2023-10-22',
    },
    '3': {
      eol: '2024-10-22',
    },
  },
};

const securityEolMetadata = {
  '@apollo/server': serverMetadata,
  'apollo-server-core': serverMetadata,
  'apollo-router': {
    securityPatches: [
      { version: '0.9.0', affectedRange: '<0.9.0' },
      { version: '0.15.1', affectedRange: '>= 0.1.0-alpha.7 - 0.15.0' },
      { version: '1.29.1', affectedRange: '1.28.1 - 1.29.0' },
      { version: '1.33.0', affectedRange: '1.31.0 - 1.32.0' },
    ],
    byMajor: {},
  },
};

// merge the generated latest version data into the static security/eol data
export const packageMetadata: PackageMetadata = merge(
  securityEolMetadata,
  latestVersionMetadata,
);

export function parseVersion(
  versionString?: string | null,
): [keyof PackageMetadata, string] | null {
  if (!versionString) return null;

  const parsed2 = versionString.match(
    /^(apollo-server-core|@apollo\/server|apollo-router)@(.*)$/,
  );
  if (!parsed2) return null;

  const [, packageName, packageVersion] = parsed2;
  if (!semverValid(packageVersion)) return null;

  return [packageName, packageVersion] as [keyof PackageMetadata, string];
}

export enum UPDATE_SEVERITY {
  UpToDate,
  Unknown,
  Patch,
  Minor,
  Major,
  Security,
}

export function determineUpdateSeverityLevel(
  packageName: keyof PackageMetadata,
  packageVersion: string,
  // for testing purposes
  packageInfoByName: PackageMetadata,
): UPDATE_SEVERITY {
  const versionInfoForPackage = packageInfoByName[packageName];
  if (!versionInfoForPackage) {
    return UPDATE_SEVERITY.Unknown;
  }

  // bail early if up-to-date (or if the reported version is _more_ current than
  // the latest known version in our packageMetadata object)
  if (semverGte(packageVersion, versionInfoForPackage.latest)) {
    return UPDATE_SEVERITY.UpToDate;
  }

  const majorVersion = semverMajor(packageVersion);

  const versionInfoForPackageMajor =
    versionInfoForPackage.byMajor[majorVersion];
  if (!versionInfoForPackageMajor) {
    return UPDATE_SEVERITY.Unknown;
  }

  // Are there security updates available?
  const securityUpdates = versionInfoForPackage.securityPatches.filter(
    ({ affectedRange }) =>
      semverSatisfies(packageVersion, affectedRange, {
        includePrerelease: true,
      }),
  );
  if (securityUpdates.length > 0) {
    return UPDATE_SEVERITY.Security;
  }

  if (majorVersion < semverMajor(versionInfoForPackage.latest)) {
    return UPDATE_SEVERITY.Major;
  }

  if (
    semverMinor(packageVersion) < semverMinor(versionInfoForPackageMajor.latest)
  ) {
    return UPDATE_SEVERITY.Minor;
  }

  if (semverLt(packageVersion, versionInfoForPackageMajor.latest)) {
    return UPDATE_SEVERITY.Patch;
  }

  Sentry.captureException(
    new Error(
      `Unable to determine update severity given package version ${packageVersion}`,
    ),
  );
  return UPDATE_SEVERITY.Unknown;
}

interface PackageInfo {
  latest: string;
  securityPatches: { version: string; affectedRange: string }[];
  byMajor: Record<
    string,
    {
      latest: string;
      eol?: string;
    }
  >;
}

export interface PackageMetadata {
  'apollo-server-core': PackageInfo;
  '@apollo/server': PackageInfo;
  'apollo-router': PackageInfo;
}
